import { take, put, call, actionChannel, fork, select } from 'redux-saga/effects';
import { buffers } from 'redux-saga';

import log from 'yeself-client-common/utils/LogUtils';

import { selectAppId } from 'Console/data/applicationDetail/settings/SettingsSelector';

import {
    FETCH_THEMES_IDS,
    FETCH_THEME,
    FETCH_THEME_SCHEMA,
    CLONE_THEME,
    EDIT_THEME,
    DELETE_THEME,
    EDIT_THEME_SUCCESS,
} from 'Console/data/applicationDetail/themes/constants';
import {
    fetchTheme as fetchThemeAction,
    fetchThemeData,
    fetchThemesIdsError,
    fetchThemesIdsData,
    fetchThemeError,
    cloneThemeSuccess,
    cloneThemeError,
    editThemeSuccess,
    editThemeError,
    deleteThemeSuccess,
    deleteThemeError,
    fetchThemeSchemaSuccess,
    fetchThemeSchemaError,
} from 'Console/data/applicationDetail/themes/actions';
import { pushNotificationToStack } from 'Console/data/notification/actions';
import {
    fetchThemesListIds,
    fetchTheme,
    cloneTheme,
    editTheme,
    deleteTheme,
    fetchThemeSchema,
} from 'Console/data/applicationDetail/themes/services';
import { mapThemeSchemaFormToDTO } from 'Console/data/applicationDetail/themes/mappers';

function* fetchThemesIdsWatcher() {
    while (true) {
        const {
            payload: { appId },
        } = yield take(FETCH_THEMES_IDS);

        try {
            const payload = yield call(fetchThemesListIds, appId);
            yield put(fetchThemesIdsData(payload.body.data));
        } catch (err) {
            log.error(err);
            yield put(fetchThemesIdsError());
        }
    }
}

function* fetchThemeWatcher() {
    const openChannel = yield actionChannel(FETCH_THEME, buffers.expanding());

    while (true) {
        const {
            payload: { appId, themeId },
        } = yield take(openChannel);

        try {
            const res = yield call(fetchTheme, appId, themeId);
            yield put(fetchThemeData(themeId, res.body.data));
        } catch (err) {
            log.error(err);
            yield put(fetchThemeError());
        }
    }
}

function* editThemeSuccessWatcher() {
    while (true) {
        const {
            payload: { themeId },
        } = yield take(EDIT_THEME_SUCCESS);
        const appId = yield select(selectAppId);

        try {
            yield put(fetchThemeAction(appId, themeId));
        } catch (err) {
            log.error(err);
        }
    }
}

function* cloneThemeWatcher() {
    while (true) {
        const { payload, message } = yield take(CLONE_THEME);
        const appId = yield select(selectAppId);
        const newThemeData = {
            ...payload.sourceTheme,
            name: `${payload.sourceTheme.name}-copy`,
        };

        try {
            const sourceThemeId = payload.sourceTheme.id;
            const resp = yield call(
                cloneTheme,
                appId,
                sourceThemeId,
                `${payload.sourceTheme.name} - cloned`
            );
            yield put(cloneThemeSuccess(resp.body));
            yield put(pushNotificationToStack(message.success));
        } catch (err) {
            log.error(err);
            yield put(cloneThemeError(payload));
            yield put(pushNotificationToStack(message.fail));
        }
    }
}

function* editThemeWatcher() {
    while (true) {
        const { payload, message } = yield take(EDIT_THEME);

        const appId = yield select(selectAppId);
        const data = mapThemeSchemaFormToDTO(payload);

        try {
            const resp = yield call(editTheme, appId, payload.themeId, data);
            yield put(editThemeSuccess(payload));
            yield put(pushNotificationToStack(message.success));
        } catch (err) {
            log.error(err);
            yield put(editThemeError(payload));
            yield put(pushNotificationToStack(message.fail));
        }
    }
}
function* deleteThemeWatcher() {
    while (true) {
        const { payload, message } = yield take(DELETE_THEME);
        const appId = yield select(selectAppId);
        try {
            yield call(deleteTheme, appId, payload);
            yield put(deleteThemeSuccess(payload));
            yield put(pushNotificationToStack(message.success));
        } catch (err) {
            log.error(err);
            yield put(deleteThemeError(payload));
            yield put(pushNotificationToStack(message.fail));
        }
    }
}

function* fetchThemeSchemaWatcher() {
    while (true) {
        const { payload } = yield take(FETCH_THEME_SCHEMA);
        const appId = yield select(selectAppId);

        try {
            const resp = yield call(fetchThemeSchema, appId, payload);
            yield put(fetchThemeSchemaSuccess(resp.body));
        } catch (err) {
            log.error(err);
            yield put(fetchThemeSchemaError(payload));
        }
    }
}

export default function* themesSaga() {
    yield fork(fetchThemeWatcher);
    yield fork(fetchThemesIdsWatcher);
    yield fork(cloneThemeWatcher);
    yield fork(editThemeWatcher);
    yield fork(deleteThemeWatcher);
    yield fork(fetchThemeSchemaWatcher);
    yield fork(editThemeSuccessWatcher);
}
